// biome-ignore-all lint/suspicious/noArrayIndexKey: file/invoice slots are index-keyed by design — a slot index is the stable identity for these fixed-length upload arrays.
// =============================================================================
// FilesPanel.jsx
// =============================================================================
// The Files & Invoices slide-in modal used in the staff view of ReqDetail.
// Extracted from RequestDetail.jsx (Phase D).
//
// Props
// ─────
//   req           — request object (refNo, children)
//   docs          — supporting-document file array
//   setDocs       — update docs
//   invoices      — invoice file array
//   setInvoices   — update invoices
//   fileUploading — map of upload-in-progress flags, keyed by "docs_i"/"invoices_i"
//   uploadFile    — (list, setList, index, file, isInvoice) → void
//   onClose       — called to close the panel
// =============================================================================

import { useEffect, useState } from "react";
import { useSession } from "../../app/SessionContext.jsx";
import {
  getFileCreatedAt,
  getFileSignedUrl,
  getInvoiceCreatedAt,
  getInvoiceSignedUrl,
} from "../../data/storage";
import { GLYPH } from "../../utils/glyphs";
import { formatRef } from "../../utils/helpers";
import { FileField, ModalBackdrop } from "../form/FormUI.jsx";
import { HelpButton } from "../form/HelpButton.jsx";

// Storage stamps created_at in UTC; show it in local time, date + time.
function fmtUploaded(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("en-AU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// The stored storage path for a doc / invoice record (string legacy path or
// an object carrying url/path). "" when there's nothing to look up.
function docPathOf(f) {
  return f ? (typeof f === "string" ? f : f.url || "") : "";
}
function invPathOf(f) {
  return f ? (typeof f === "string" ? f : f.path || f.url || "") : "";
}

export function FilesPanel({
  req,
  docs,
  setDocs,
  invoices,
  setInvoices,
  fileUploading,
  uploadFile,
  onClose,
}) {
  const { user } = useSession();
  // path -> created_at ISO. Read from Storage (retroactive; nothing persisted).
  const [uploadDates, setUploadDates] = useState({});
  useEffect(
    function () {
      let alive = true;
      const docPaths = (docs || []).map(docPathOf).filter(Boolean);
      const invPaths = (invoices || []).map(invPathOf).filter(Boolean);
      async function run() {
        const next = {};
        await Promise.all([
          ...docPaths.map(async function (p) {
            const ca = await getFileCreatedAt(p);
            if (ca) next[p] = ca;
          }),
          ...invPaths.map(async function (p) {
            const ca = await getInvoiceCreatedAt(p);
            if (ca) next[p] = ca;
          }),
        ]);
        if (alive) setUploadDates(next);
      }
      run();
      return function () {
        alive = false;
      };
    },
    [docs, invoices]
  );
  return (
    <ModalBackdrop
      onClose={onClose}
      contentStyle={{
        background: "white",
        width: "calc(100vw - 40px)",
        maxWidth: 860,
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 8px 40px rgba(0,0,0,0.3)",
        borderRadius: 12,
        overflow: "hidden",
        fontFamily: "inherit",
      }}
    >
      <div
        style={{
          background: "#546E7A",
          color: "white",
          padding: "14px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 16 }}>
          Files &amp; Invoices — {formatRef(req.refNo)}
        </div>
        <button
          onClick={onClose}
          style={{
            background: "rgba(255,255,255,0.2)",
            border: "1px solid rgba(255,255,255,0.4)",
            borderRadius: 6,
            color: "white",
            fontSize: 16,
            fontWeight: 700,
            cursor: "pointer",
            lineHeight: 1,
            padding: "4px 10px",
          }}
        >
          X
        </button>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "20px" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 20,
            alignItems: "start",
          }}
        >
          <div>
            <div
              style={{
                fontWeight: 700,
                fontSize: 15,
                color: "#1565C0",
                marginBottom: 12,
                paddingBottom: 8,
                borderBottom: "2px solid #90CAF9",
              }}
            >
              Supporting Documents
            </div>
            {docs.map(function (f, i) {
              if (!f)
                return (
                  <div key={i} style={{ marginBottom: 10 }}>
                    <FileField
                      label={`Document ${i + 1}`}
                      file={null}
                      onChange={function (file) {
                        uploadFile(docs, setDocs, i, file, false);
                      }}
                      uploading={fileUploading[`docs_${i}`]}
                    />
                  </div>
                );
              var displayName =
                typeof f === "string"
                  ? f
                      .split("/")
                      .pop()
                      .replace(/^[^_]+_/, "")
                  : f.name || f.url?.split("/").pop() || `Document ${i + 1}`;
              var fileUrl = typeof f === "string" ? f : f.url;
              var docLabel = typeof f === "object" && f.label ? f.label : null;
              if (!fileUrl) return null;
              if (typeof f === "string" && f.includes("Authority_") && f.endsWith(".html"))
                return null;
              if (
                typeof f === "object" &&
                f.name &&
                f.name.endsWith(".html") &&
                f.type === "authority"
              )
                return null;
              return (
                <div
                  key={i}
                  style={{
                    background: "#E3F2FD",
                    borderRadius: 7,
                    marginBottom: 7,
                    padding: "8px 12px",
                  }}
                >
                  {docLabel && (
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: "#1565C0",
                        marginBottom: 3,
                      }}
                    >
                      {docLabel}
                    </div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <span
                        style={{
                          fontSize: 13,
                          color: "#1A2E2B",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          display: "block",
                        }}
                      >
                        {GLYPH.page} {displayName || `Document ${i + 1}`}
                      </span>
                      {uploadDates[fileUrl] && (
                        <span
                          style={{ fontSize: 11, color: "#607D79", display: "block", marginTop: 2 }}
                        >
                          Uploaded {fmtUploaded(uploadDates[fileUrl])}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={async function () {
                        // Private bucket (db-168): sign the stored PATH on demand
                        // (tolerates a legacy full URL too).
                        const url = await getFileSignedUrl(fileUrl);
                        if (url) window.open(url, "_blank", "noopener,noreferrer");
                      }}
                      style={{
                        flexShrink: 0,
                        background: "#1565C0",
                        border: "none",
                        cursor: "pointer",
                        borderRadius: 5,
                        padding: "4px 12px",
                        fontSize: 12,
                        color: "white",
                        fontWeight: 600,
                      }}
                    >
                      {GLYPH.arrowDown} Download
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <div>
            <div
              style={{
                fontWeight: 700,
                fontSize: 20,
                color: "#00695C",
                marginBottom: 12,
                paddingBottom: 8,
                borderBottom: "2px solid #A5D6A7",
              }}
            >
              Invoices &amp; Receipts
              {user && <HelpButton helpKey="files_invoices" user={user} />}
            </div>
            {invoices.map(function (f, i) {
              if (!f)
                return (
                  <FileField
                    key={i}
                    label={`Invoice ${i + 1}`}
                    file={null}
                    onChange={function (file) {
                      uploadFile(invoices, setInvoices, i, file, true);
                    }}
                    uploading={fileUploading[`invoices_${i}`]}
                  />
                );
              var displayName =
                typeof f === "string"
                  ? f
                      .split("/")
                      .pop()
                      .replace(/^[^_]+_/, "")
                  : f.name;
              const invUrl = invPathOf(f);
              return (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "8px 12px",
                    background: "#E0F2F1",
                    borderRadius: 7,
                    marginBottom: 7,
                    gap: 8,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <span
                      style={{
                        fontSize: 13,
                        color: "#1A2E2B",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                      }}
                    >
                      {GLYPH.receipt} {displayName || `Invoice ${i + 1}`}
                    </span>
                    {uploadDates[invUrl] && (
                      <span
                        style={{ fontSize: 11, color: "#607D79", display: "block", marginTop: 2 }}
                      >
                        Uploaded {fmtUploaded(uploadDates[invUrl])}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={async function () {
                      // Invoices are private (db-26): sign the stored PATH on demand
                      // (tolerate an old full URL too, just in case).
                      const url =
                        typeof f === "string" && f.startsWith("http")
                          ? f
                          : await getInvoiceSignedUrl(f);
                      if (url) window.open(url, "_blank", "noopener,noreferrer");
                    }}
                    style={{
                      flexShrink: 0,
                      background: "#00695C",
                      border: "none",
                      cursor: "pointer",
                      borderRadius: 5,
                      padding: "4px 12px",
                      fontSize: 12,
                      color: "white",
                      fontWeight: 600,
                      fontFamily: "inherit",
                    }}
                  >
                    {GLYPH.arrowDown} Download
                  </button>
                </div>
              );
            })}
            <div style={{ marginTop: 8, fontSize: 12, color: "#607D79", fontStyle: "italic" }}>
              Uploading an invoice triggers an automatic email to invoicing@zephyreducation.com.au
            </div>
          </div>
        </div>
      </div>
    </ModalBackdrop>
  );
}
